/**
 * Traduce los splitters tal como los guarda NAPConfigForm (node.config.splitters)
 * al shape que espera InternalConnectionsDiagram (SplitterCompleto de types/nodes.ts).
 *
 * No es un modelo de datos nuevo ni una copia persistida -- se recalcula en cada
 * render a partir del mismo config real, para no repetir el problema de mantener
 * dos representaciones de "qué es un splitter" que puedan divergir.
 * @module utils/napSplitterDiagramAdapter
 */
import { SplitterCompleto, SplitterPortSalida, EstadoPuertoSplitter, TipoSplitter, RatioSplitter, RatioDesbalanceado } from '@/types/nodes'
import { salidaLossDb } from '@/services/opticalBudgetService'

const TIA_598_COLORES = [
  'azul', 'naranja', 'verde', 'marron', 'gris', 'blanco',
  'rojo', 'negro', 'amarillo', 'violeta', 'rosa', 'turquesa',
]

const colorPorPuerto = (puerto: number) => TIA_598_COLORES[(puerto - 1) % 12]

interface NapSplitterRaw {
  id: string
  tipoSplitter?: TipoSplitter
  ratio?: RatioSplitter
  ratioDesbalanceado?: RatioDesbalanceado
  atenuacion?: number
  puertosDesbalanceados?: Array<{ puerto: number; atenuacion: number }>
  entrada?: {
    tipoEntrada?: 'cable' | 'splitter'
    cableId?: string
    bufferNum?: number
    fibraNum?: number
    fibraColor?: string
    splitterOrigenId?: string
    puertoSalidaOrigen?: number
  }
  salidas?: Array<{
    puerto: number
    estado?: 'libre' | 'conectado' | 'reservado'
    destino?: {
      tipo?: 'cable' | 'puerto_nap' | 'splitter'
      cableId?: string
      bufferNum?: number
      fibraNum?: number
      puertoNAP?: number
      splitterDestinoId?: string
    }
  }>
}

function estadoSalida(estado?: string): EstadoPuertoSplitter {
  if (estado === 'conectado') return 'ocupado'
  if (estado === 'reservado') return 'reservado'
  return 'libre'
}

/**
 * Convierte una salida en cascada al color de pigtail que le corresponde en
 * el splitter padre, para que el punto de entrada del hijo se pinte igual
 * que el punto de salida que lo alimenta.
 */
function colorEntradaCascada(padre: NapSplitterRaw | undefined, puertoOrigen: number | undefined): string {
  if (padre && puertoOrigen != null) return colorPorPuerto(puertoOrigen)
  return 'gris'
}

export function napSplittersToSplitterCompleto(raw: unknown, nodoId: string): SplitterCompleto[] {
  if (!Array.isArray(raw)) return []
  const splitters = raw as NapSplitterRaw[]
  const byId = new Map(splitters.map((s) => [s.id, s]))

  return splitters.map((s): SplitterCompleto => {
    const vieneDeSplitter = s.entrada?.tipoEntrada === 'splitter'
    const padre = vieneDeSplitter ? byId.get(s.entrada?.splitterOrigenId || '') : undefined

    const entrada = vieneDeSplitter
      ? {
          cableId: '',
          bufferNum: 0,
          fibraNum: s.entrada?.puertoSalidaOrigen ?? 0,
          colorBuffer: 'azul',
          colorFibra: colorEntradaCascada(padre, s.entrada?.puertoSalidaOrigen),
          estadoFibraOrigen: 'derivado' as const,
        }
      : {
          cableId: s.entrada?.cableId || '',
          bufferNum: s.entrada?.bufferNum ?? 0,
          fibraNum: s.entrada?.fibraNum ?? 0,
          colorBuffer: 'azul',
          // Compat: versiones antiguas guardaban esto en `entradaFibra` en vez de `entrada`.
          colorFibra: s.entrada?.fibraColor || (s as { entradaFibra?: { fibraColor?: string } }).entradaFibra?.fibraColor || 'gris',
          estadoFibraOrigen: 'derivado' as const,
        }

    const salidas: SplitterPortSalida[] = (s.salidas || []).map((sal) => {
      const d = sal.destino
      let destino: SplitterPortSalida['destino']
      if (d?.tipo === 'splitter' && d.splitterDestinoId) {
        destino = { tipo: 'splitter_hijo', splitterDestinoId: d.splitterDestinoId }
      } else if (d?.tipo === 'puerto_nap') {
        destino = { tipo: 'puerto_nap', puertoNapNum: d.puertoNAP }
      } else if (d?.tipo === 'cable') {
        destino = { tipo: 'empalme_cable', cableId: d.cableId, bufferNum: d.bufferNum, fibraNum: d.fibraNum }
      }

      return {
        numero: sal.puerto,
        colorPigtail: colorPorPuerto(sal.puerto),
        estado: estadoSalida(sal.estado),
        atenuacion: salidaLossDb(s, sal.puerto),
        destino,
      }
    })

    return {
      id: s.id,
      tipo: s.tipoSplitter || 'balanceado',
      ratio: (s.ratio || '1:8') as RatioSplitter,
      ratioDesbalanceado: s.ratioDesbalanceado,
      nivel: vieneDeSplitter ? 2 : 1,
      nodoId,
      entrada,
      salidas,
      atenuacionBase: typeof s.atenuacion === 'number' ? s.atenuacion : 0,
    }
  })
}
